// service/api_elcristo.ts - VERSIÓN CORREGIDA COMPLETA
// Servicio API para consumir contenido dinámico del CMS

// ============================================================================
// TYPES
// ============================================================================

export interface MenuItem {
  text: string;
  url: string;
  image?: string;
  active?: boolean;
  subMenu?: MenuItem[];
}

export interface ContactInfo {
  address: string;
  email: string;
  phone?: string;
  schedule?: string;
}

export interface SocialLinks {
  facebook: string;
  linkedin: string;
  instagram?: string;
  youtube: string;
  twitter?: string;
}

export interface CTAButton {
  text: string;
  url: string;
  image?: string;
}

export interface ChatConfig {
  apiUrl?: string;
  apiKey?: string;
  botName?: string;
  welcomeMessage?: string;
  enabled?: boolean;
  position?: 'bottom-right' | 'bottom-left';
  primaryColor?: string;
  buttonSize?: 'small' | 'medium' | 'large';
  theme?: 'light' | 'dark';
}

export interface ModalConfig {
  enabled: boolean;
  title: string;
  message: string;
  documentPath?: string;
  logo: string;
  showDate: boolean;
  date: string;
  showSignature?: boolean;
  signatureText?: string[];
  showOnLoad?: boolean;
  startDate: string;
  endDate: string;
}

export interface HeaderData {
  logo: string;
  contactInfo: ContactInfo;
  socialLinks: SocialLinks;
  menuItems: MenuItem[];
  ctaButton: CTAButton;
  chatConfig?: ChatConfig;
  modalConfig?: ModalConfig;
}

export interface OffcanvasData {
  logo: string;
  description: string;
  contactInfo: ContactInfo;
  socialLinks: SocialLinks;
  ctaButton: CTAButton;
}

export interface HeroData {
  title: string;
  subtitle: string;
  description: string;
  primaryButtonText: string;
  primaryButtonLink: string;
  secondaryButtonText: string;
  secondaryButtonLink: string;
  heroImage: string;
  heroBackgroundImage: string;
}

export interface Feature {
  image: string;
  title: string;
}

export interface FeaturesData {
  features: Feature[];
}

export interface BulletPoint {
  text: string;
  url: string;
}

export interface AboutUsData {
  subtitle: string;
  title: string;
  description: string;
  bulletPoints: BulletPoint[];
  exploreMoreLink: string;
  contactNumber: string;
  images: {
    bus: string;
    girl: string;
    dot: string;
    main: string;
    secondary: string;
    borderShape: string;
  };
}

export interface VideoData {
  backgroundImage: string;
  videoUrl: string;
}

export interface Program {
  image: string;
  title: string;
  ageRange: string;
  description: string;
  link: string;
  backgroundColor: 'default' | 'bg-2' | 'bg-4';
}

export interface ProgramsData {
  title: string;
  subtitle: string;
  programs: Program[];
}

export interface WorkProcessItem {
  icon: string;
  title: string;
  description: string;
  lineShape?: string;
  iconBackground: string;
  style: 'default' | 'style-2';
}

export interface WorkProcessData {
  workProcessItems: WorkProcessItem[];
}

export interface Activity {
  icon: string;
  title: string;
  description: string;
  iconColor: 'box-color-1' | 'box-color-2' | 'box-color-3' | 'box-color-4';
}

export interface ActivitiesData {
  subtitle: string;
  title: string;
  wordsbutton: string;
  wordslink: string;
  description: string;
  image: string;
  radiusShape: string;
  activities: Activity[];
}

export interface TeamMember {
  image: string;
  name: string;
  role: string;
  teamShape?: string;
  socialLinks: {
    facebook: string;
    twitter: string;
    youtube: string;
    linkedin: string;
  };
}

export interface TeamData {
  subtitle: string;
  title: string;
  teamMembers: TeamMember[];
}

export interface Testimonial {
  quote: string;
  author: string;
  quoteIcon?: string;
  backgroundStyle?: 'default' | 'bg-2' | 'bg-3';
}

export interface TestimonialData {
  subtitle: string;
  title: string;
  testimonials: Testimonial[];
}

export interface CTAData {
  subtitle?: string;
  title?: string;
  backgroundImage?: string;
  ctaImage?: string;
  ctaShape?: string;
  ctaButtonText?: string;
  ctaButtonLink?: string;
  planeShape?: string;
  pencilShape?: string;
}

export interface Author {
  name: string;
  image: string;
  role?: string;
}

export interface NewsItem {
  image: string;
  category: string;
  date: string;
  title: string;
  description?: string;
  link: string;
  author: Author;
}

export interface NewsData {
  subtitle: string;
  title: string;
  seeAllLink?: string;
  featuredNews: NewsItem;
  additionalNews: NewsItem[];
}

export interface InstagramPost {
  image: string;
  link: string;
}

export interface InstagramData {
  title: string;
  instagram: string;
  posts: InstagramPost[];
}

export interface NewsletterData {
  subtitle: string;
  title: string;
  placeholderText: string;
  backgroundImage: string;
}

export interface Brand {
  name?: string;
  image: string;
  url?: string;
}

export interface BrandsData {
  title: string;
  subtitle?: string;
  brands: Brand[];
}

export interface QuickLink {
  text: string;
  url: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

export interface Category {
  text: string;
  url: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

export interface ContactInfoItem {
  icon: string;
  title: string;
  description: string;
  link: string;
}

export interface RecentPost {
  image: string;
  date: string;
  title: string;
  link: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

export interface FooterData {
  logo: string;
  description: string;
  quickLinks: QuickLink[];
  categories: Category[];
  contactInfo: ContactInfoItem[];
  recentPosts: RecentPost[];
  copyrightText: string;
  termsLink: string;
  privacyLink: string;
  socialLinks: SocialLinks;
}

export interface SiteContent {
  header: HeaderData;
  offcanvas: OffcanvasData;
  hero: HeroData;
  features: FeaturesData;
  aboutUs: AboutUsData;
  video: VideoData;
  programs: ProgramsData;
  workProcess: WorkProcessData;
  activities: ActivitiesData;
  team: TeamData;
  testimonial: TestimonialData;
  cta: CTAData;
  news: NewsData;
  instagram: InstagramData;
  newsletter?: NewsletterData;
  brands?: BrandsData;
  footer?: FooterData;
  modalConfig?: ModalConfig; 
  last_updated?: string;
  last_updated_by?: string;
}

// ============================================================================
// API CLIENT
// ============================================================================

class APIElCristoClient {
  private baseURL: string;
  private token: string | null = null;

  constructor(baseURL: string = 'https://backend.anubisai.net/api/elcristo') {
    this.baseURL = baseURL;
    this.loadToken();
  }

  private loadToken(): void {
    this.token = localStorage.getItem('elcristo_token');
  }

  private saveToken(token: string): void {
    this.token = token;
    localStorage.setItem('elcristo_token', token);
  }

  private clearToken(): void {
    this.token = null;
    localStorage.removeItem('elcristo_token');
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    this.loadToken(); 
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${this.baseURL}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      if (response.status === 401) {
        this.clearToken();
      }
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  // ============================================================================
  // AUTH
  // ============================================================================

  async login(email: string, password: string): Promise<{ access_token: string; refresh_token: string; user: any }> {
    const data = await this.request<{ access_token: string; refresh_token: string; token_type: string; user: any }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    
    this.saveToken(data.access_token);
    localStorage.setItem('elcristo_token', data.access_token);
    localStorage.setItem('elcristo_refresh_token', data.refresh_token);
    
    return data;
  }

  async logout(): Promise<void> {
    this.clearToken();
  }

  isAuthenticated(): boolean {
    return !!this.token;
  }

  // ============================================================================
  // CONTENT ENDPOINTS (GET)
  // ============================================================================

  async getSiteContent(): Promise<SiteContent> {
    return this.request<SiteContent>('/content');
  }

  async getHeaderData(): Promise<HeaderData> {
    return this.request<HeaderData>('/content/header');
  }

  async getOffcanvasData(): Promise<OffcanvasData> {
    return this.request<OffcanvasData>('/content/offcanvas');
  }

  async getHeroData(): Promise<HeroData> {
    return this.request<HeroData>('/content/hero');
  }

  async getFeaturesData(): Promise<FeaturesData> {
    return this.request<FeaturesData>('/content/features');
  }

  async getAboutUsData(): Promise<AboutUsData> {
    return this.request<AboutUsData>('/content/aboutUs'); // ← FIX: aboutUs (camelCase)
  }

  async getVideoData(): Promise<VideoData> {
    return this.request<VideoData>('/content/video');
  }

  async getProgramsData(): Promise<ProgramsData> {
    return this.request<ProgramsData>('/content/programs');
  }

  async getWorkProcessData(): Promise<WorkProcessData> {
    return this.request<WorkProcessData>('/content/workProcess'); // ← FIX: workProcess (camelCase)
  }

  async getActivitiesData(): Promise<ActivitiesData> {
    return this.request<ActivitiesData>('/content/activities');
  }

  async getTeamData(): Promise<TeamData> {
    return this.request<TeamData>('/content/team');
  }

  async getTestimonialData(): Promise<TestimonialData> {
    return this.request<TestimonialData>('/content/testimonial');
  }

  async getCTAData(): Promise<CTAData> {
    return this.request<CTAData>('/content/cta');
  }

  async getNewsData(): Promise<NewsData> {
    return this.request<NewsData>('/content/news');
  }

  async getInstagramData(): Promise<InstagramData> {
    return this.request<InstagramData>('/content/instagram');
  }

  // ============================================================================
  // ADMIN ENDPOINTS (CRUD) - DEPRECATED
  // Estos endpoints individuales pueden dar error 422
  // Se recomienda usar updateSiteContent() en su lugar
  // ============================================================================

  async updateHeaderData(data: HeaderData): Promise<HeaderData> {
    return this.request<HeaderData>('/admin/content/header', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async updateOffcanvasData(data: OffcanvasData): Promise<OffcanvasData> {
    return this.request<OffcanvasData>('/admin/content/offcanvas', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async updateHeroData(data: HeroData): Promise<HeroData> {
    return this.request<HeroData>('/admin/content/hero', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async updateFeaturesData(data: FeaturesData): Promise<FeaturesData> {
    return this.request<FeaturesData>('/admin/content/features', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async updateAboutUsData(data: AboutUsData): Promise<AboutUsData> {
    return this.request<AboutUsData>('/admin/content/aboutUs', { // ← FIX: aboutUs (camelCase)
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async updateVideoData(data: VideoData): Promise<VideoData> {
    return this.request<VideoData>('/admin/content/video', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async updateProgramsData(data: ProgramsData): Promise<ProgramsData> {
    return this.request<ProgramsData>('/admin/content/programs', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async updateWorkProcessData(data: WorkProcessData): Promise<WorkProcessData> {
    return this.request<WorkProcessData>('/admin/content/workProcess', { // ← FIX: workProcess (camelCase)
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async updateActivitiesData(data: ActivitiesData): Promise<ActivitiesData> {
    return this.request<ActivitiesData>('/admin/content/activities', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async updateTeamData(data: TeamData): Promise<TeamData> {
    return this.request<TeamData>('/admin/content/team', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async updateTestimonialData(data: TestimonialData): Promise<TestimonialData> {
    return this.request<TestimonialData>('/admin/content/testimonial', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async updateCTAData(data: CTAData): Promise<CTAData> {
    return this.request<CTAData>('/admin/content/cta', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async updateNewsData(data: NewsData): Promise<NewsData> {
    return this.request<NewsData>('/admin/content/news', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async updateInstagramData(data: InstagramData): Promise<InstagramData> {
    return this.request<InstagramData>('/admin/content/instagram', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // ============================================================================
  // USER MANAGEMENT
  // ============================================================================

  async getUsers(): Promise<any> {
    return this.request<any>('/admin/users', {
      method: 'GET',
    });
  }

  async createUser(userData: { email: string; name: string; role: string; password: string }): Promise<any> {
    return this.request<any>('/admin/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async updateUser(userId: string, userData: any): Promise<any> {
    return this.request<any>(`/admin/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  async deleteUser(userId: string): Promise<any> {
    return this.request<any>(`/admin/users/${userId}`, {
      method: 'DELETE',
    });
  }

  // ============================================================================
  // UNIVERSAL UPDATE (RECOMENDADO)
  // ============================================================================

  /**
   * Actualiza contenido del sitio (acepta objeto parcial)
   * USO RECOMENDADO: Usar este método en vez de los update individuales
   * para evitar errores 422
   * 
   * Ejemplos:
   *   updateSiteContent({ header: headerData })
   *   updateSiteContent({ hero: heroData })
   *   updateSiteContent({ aboutUs: aboutUsData })
   */
  async updateSiteContent(updates: any): Promise<any> {
    return this.request<any>('/content', {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  /**
   * Restaura el contenido a valores por defecto
   */
  async restoreDefaultContent(): Promise<any> {
    return this.request<any>('/restore-default', {
      method: 'POST',
    });
  }

  /**
   * Lista los backups disponibles
   */
  async listBackups(): Promise<any> {
    return this.request<any>('/backups', {
      method: 'GET',
    });
  }
}

// ============================================================================
// EXPORT SINGLETON
// ============================================================================

export const apiElCristo = new APIElCristoClient();
export default apiElCristo;